import InformationDisplay, { formatInformationTime } from "@/components/InformationDisplay";
import { getClientIdentityHeaders } from "@/lib/clientIdentity";
import type { WhoResearchContext as WhoResearchContextDto, WhoResearchContextItem } from "@shared/intelligenceReadiness";
import React, { useMemo, useState } from "react";

interface SavedReportResponse {
  status: "ok";
  reportId: string;
  report: { id: string };
}

export async function saveWhoResearchSnapshot(revisionKeys: string[]): Promise<SavedReportResponse> {
  const response = await fetch("/api/intelligence/research-snapshots", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getClientIdentityHeaders() },
    body: JSON.stringify({ revisionKeys }),
  });
  const payload = await response.json().catch(() => null) as Partial<SavedReportResponse> & { error?: string; detail?: string } | null;
  if (!response.ok) {
    const message = response.status === 400 ? "Select valid ready WHO reports and try again."
      : response.status === 409 ? "WHO evidence changed or is no longer ready. Refresh Monitor before saving."
        : response.status === 503 ? "WHO research snapshots are unavailable or disabled."
        : response.status === 429 ? "WHO snapshot save limit reached. Wait before trying again."
          : response.status === 401 || response.status === 403 ? "This save needs a valid browser identity and same-origin request. Refresh Monitor and try again."
          : payload?.detail ?? payload?.error ?? `Could not save WHO evidence (${response.status}).`;
    throw new Error(message);
  }
  if (!payload?.reportId || !payload.report?.id) throw new Error("The saved report response was incomplete. Refresh Reports to check the archive.");
  return payload as SavedReportResponse;
}

function originalTime(value: WhoResearchContextItem["observation"]["publishedAt"]): string {
  return value ? formatInformationTime(value) : "Unknown";
}

function sourceStateMessage(context: WhoResearchContextDto): string | null {
  if (context.status === "disabled") return context.reason ?? "WHO research context is disabled.";
  if (context.status === "unavailable") return context.reason ?? "WHO research context is unavailable.";
  if (context.status === "not-ready") return context.reason ?? "WHO research context is not ready.";
  if (context.status === "empty") return context.reason ?? "No WHO reports are available for research context.";
  if (context.status === "partial") return context.reason ?? "Some WHO reports are excluded from research context.";
  return null;
}

export default function WhoResearchContext({ context }: { context?: WhoResearchContextDto }) {
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedReportId, setSavedReportId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const groups = useMemo(() => {
    const byGroup = new Map<string, WhoResearchContextItem[]>();
    for (const item of context?.reports ?? []) {
      const group = byGroup.get(item.groupId) ?? [];
      group.push(item);
      byGroup.set(item.groupId, group);
    }
    return [...byGroup.entries()];
  }, [context?.reports]);
  const contextAllowsSave = context?.status === "ready" || context?.status === "partial";
  const readyItems = contextAllowsSave ? (context?.reports ?? []).filter((item) => item.readiness.status === "ready") : [];
  const selectedReadyKeys = readyItems.filter((item) => selectedKeys.includes(item.observation.revisionKey)).map((item) => item.observation.revisionKey);
  const message = context ? sourceStateMessage(context) : "WHO research context status is unavailable.";

  const save = async (revisionKeys: string[]) => {
    if (saving || revisionKeys.length === 0) return;
    setSaving(true);
    setSaveError(null);
    setSavedReportId(null);
    try {
      const result = await saveWhoResearchSnapshot(revisionKeys);
      setSavedReportId(result.reportId);
      setSelectedKeys([]);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Could not save WHO evidence.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (revisionKey: string) => setSelectedKeys((current) => current.includes(revisionKey)
    ? current.filter((key) => key !== revisionKey)
    : [...current, revisionKey]);

  return <section className="monitor-panel who-research-context" aria-labelledby="who-research-context-title">
    <div className="monitor-panel-head">
      <div>
        <p className="home-kicker">WHO RESEARCH CONTEXT</p>
        <h2 id="who-research-context-title">Provider-reported evidence</h2>
      </div>
      <span>{context?.counts.readyReports ?? 0} ready · {context?.counts.excludedReports ?? 0} excluded</span>
    </div>
    <p>Historical WHO reports are context only. They do not confirm a new outbreak or market impact.</p>
    {context?.information && <InformationDisplay information={context.information} />}
    {message && <p role="status">{message}</p>}
    {context?.exclusions?.length ? <ul aria-label="WHO reports excluded from research context">
      {context.exclusions.map((exclusion) => <li key={exclusion.reason}>{exclusion.reason} · {exclusion.count}</li>)}
    </ul> : null}
    {groups.map(([groupId, reports]) => {
      const ready = contextAllowsSave ? reports.filter((item) => item.readiness.status === "ready") : [];
      return <section key={groupId} aria-label={`WHO report group ${groupId}`} className="who-research-group">
        <div className="monitor-panel-head">
          <h3>{reports[0]?.observation.title || "WHO report group"}</h3>
          <button type="button" className="analytics-action-btn" onClick={() => save(ready.map((item) => item.observation.revisionKey))} disabled={saving || ready.length === 0}>
            {saving ? "Saving…" : `Save group (${ready.length} ready)`}
          </button>
        </div>
        {reports.map((item) => {
          const observation = item.observation;
          const isReady = item.readiness.status === "ready";
          return <article key={observation.revisionKey} className="who-research-report">
            <label>
              <input type="checkbox" checked={selectedKeys.includes(observation.revisionKey)} onChange={() => toggle(observation.revisionKey)} disabled={!isReady || !contextAllowsSave || saving} />
              <span>{observation.title}</span>
            </label>
            <p>{observation.summary}</p>
            <p>WHO report {observation.providerReportId} · Published: {originalTime(observation.publishedAt)} · Provider updated: {originalTime(observation.sourceUpdatedAt)} · First seen: {formatInformationTime(observation.firstSeenAt)}</p>
            <a href={observation.sourceUrl} target="_blank" rel="noreferrer">Open original WHO report</a>
            {item.readiness.reasons.length > 0 && <p role="status">Research readiness: {item.readiness.status}. {item.readiness.reasons.join("; ")}</p>}
            <InformationDisplay information={item.information} expandable={false} />
          </article>;
        })}
      </section>;
    })}
    {contextAllowsSave && readyItems.length === 0 && <p role="status">No ready WHO reports are available to save.</p>}
    {readyItems.length > 0 && <div className="reports-detail-actions">
      <button type="button" className="analytics-action-btn" onClick={() => save(selectedReadyKeys)} disabled={saving || selectedReadyKeys.length === 0}>
        {saving ? "Saving…" : `Save selected (${selectedReadyKeys.length})`}
      </button>
    </div>}
    {saveError && <p role="alert" className="analytics-error">{saveError}</p>}
    {savedReportId && <p role="status">WHO evidence saved. <a href={`/reports?id=${encodeURIComponent(savedReportId)}`}>Open saved report</a></p>}
  </section>;
}
